import { type FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthCard } from "@/components/auth/AuthCard";
import { GoogleLoginButton } from "@/components/auth/GoogleLoginButton";
import {
  loginErrorMessage,
  loginWithEmail,
  loginWithGoogle,
  useAuthUser,
} from "@/utils/firebase";
import { pathAfterLogin } from "@/utils/redirect";

const INPUT_CLASS =
  "w-full rounded-lg border border-[#CBD5E1] px-4 py-2 text-[#0F172A] outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500";
const LABEL_CLASS = "mb-2 block text-sm font-medium text-[#2D3748]";

export const LoginPage = () => {
  const authUser = useAuthUser();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (authUser === undefined) {
    return null;
  }

  // Logged in, either just now or before opening this page.
  if (authUser) {
    return <Navigate to={pathAfterLogin(location.search)} replace />;
  }

  const logIn = async (loginWithProvider: () => Promise<void>) => {
    setError(null);
    setIsLoggingIn(true);
    try {
      await loginWithProvider();
    } catch (loginError) {
      setError(loginErrorMessage(loginError));
      setIsLoggingIn(false);
    }
  };

  const handleEmailLogin = (event: FormEvent) => {
    event.preventDefault();
    void logIn(() => loginWithEmail(email.trim(), password));
  };

  return (
    <AuthCard title="Log in to Track Labeling">
      <form className="space-y-4" onSubmit={handleEmailLogin}>
        <div className="text-left">
          <label htmlFor="email" className={LABEL_CLASS}>
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            required
            className={INPUT_CLASS}
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
          />
        </div>
        <div className="text-left">
          <label htmlFor="password" className={LABEL_CLASS}>
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder="******"
            required
            className={INPUT_CLASS}
            value={password}
            onChange={(event) => setPassword(event.currentTarget.value)}
          />
        </div>
        <button
          type="submit"
          className="w-full cursor-pointer rounded-lg bg-[#7C3AED] px-4 py-3 font-medium text-white transition hover:bg-[#6D28D9] disabled:cursor-default disabled:bg-[#94A3B8]"
          disabled={isLoggingIn}
        >
          {isLoggingIn ? "Logging in..." : "Log in with Email"}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-[#CBD5E1]" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-[#0F172A]">or</span>
        </div>
      </div>

      <GoogleLoginButton
        disabled={isLoggingIn}
        onClick={() => void logIn(loginWithGoogle)}
      />

      {error && <p className="font-medium text-red-500">{error}</p>}
    </AuthCard>
  );
};
